/**
 * Mythium Configurator — main entry
 * 
 * State management + UI orchestration. Currently uses local blob URLs as preview;
 * backend upload + SVG-rendering get added in fase 3 + 4.
 */

(function () {
  'use strict';

  // ── Config ─────────────────────────────────────────────────────────────
  const BACKEND_URL = (window.MYTHIUM_CONFIG && window.MYTHIUM_CONFIG.backendUrl) || 'https://mythium-api-production.up.railway.app';

  // minLong / minShort are validated against the longest / shortest side of
  // the upload (orientation-independent). mm stays for the preview renderer.
  // priceCents + variantId are seeded with the historical hardcoded values and
  // overridden per size by applyInjectedPricing() from the live Shopify variant
  // data injected server-side; if that data is missing these stay as fallback.
  const SIZES = {
    small:  { mm: 35, priceCents: 900,  variantId: 59577352356174, minLong: 400, minShort: 200 },
    medium: { mm: 50, priceCents: 1100, variantId: 59577352388942, minLong: 450, minShort: 250 },
    large:  { mm: 70, priceCents: 1300, variantId: 59577352421710, minLong: 650, minShort: 350 },
  };

  // Override SIZES prices (and variant IDs) with the live variant data the
  // section injects as a JSON <script data-mythium-pricing>. Per size: only
  // apply when the variant was found and carries a valid price, otherwise keep
  // the hardcoded fallback and warn — the configurator must never break.
  function applyInjectedPricing() {
    const el = (root || document).querySelector('[data-mythium-pricing]');
    if (!el) return;
    let data;
    try {
      data = JSON.parse(el.textContent);
    } catch (e) {
      console.warn('[mythium] could not parse injected pricing data; using fallback prices', e);
      return;
    }
    Object.keys(SIZES).forEach((key) => {
      const inj = data && data[key];
      if (inj && inj.found === true && typeof inj.priceCents === 'number' && inj.priceCents > 0) {
        SIZES[key].priceCents = inj.priceCents;
        if (typeof inj.variantId === 'number' && inj.variantId > 0) {
          SIZES[key].variantId = inj.variantId;
        }
      } else {
        console.warn('[mythium] variant price for size "' + key + '" not found; using fallback ' + SIZES[key].priceCents + ' cents');
      }
    });
  }

  const FILE_LIMITS = {
    maxBytes: 50 * 1024 * 1024,
    allowedTypes: ['image/png', 'image/jpeg'],
  };

  // ── State ──────────────────────────────────────────────────────────────
  const state = {
    minis: [],
    editingMiniId: null,
    draft: createEmptyDraft(),
    isCheckingOut: false,
  };

  function createEmptyDraft() {
    return {
      file: null,
      previewUrl: null,
      dimensions: null,
      size: 'medium',
      quantity: 1,
      error: null,
      processingMessage: null,
      processingPercent: 0,
      processingDisplayUrl: null,
      // Alpha analysis result (filled by analyzer)
      croppedPreviewUrl: null,
      cutlineMaskUrl: null,
      rimColorUrl: null,
      rimPadFraction: 0,
      aspectRatio: null,
      hasAlpha: false,
      alphaCenterX: 0.5,
      alphaBottomFraction: 1.0,
      footLeftFraction: 0,
      footRightFraction: 1,
      isAnalyzing: false,
      analyzeToken: 0,
    };
  }

  // ── DOM references ─────────────────────────────────────────────────────
  let root, dropzone, fileInput, errorEl, previewEl;
  let sizeSelector, baseIndicatorEl, quantityInput, addBtn;
  let setListEl, setEmptyEl, setTotalsEl, setTotalEl, checkoutBtn;

  function cacheDom() {
    root           = document.querySelector('.mythium-configurator');
    if (!root) return false;

    dropzone       = root.querySelector('[data-mythium-dropzone]');
    fileInput      = root.querySelector('[data-mythium-file-input]');
    errorEl        = root.querySelector('[data-mythium-error]');
    previewEl      = root.querySelector('[data-mythium-preview]');
    sizeSelector   = root.querySelector('[data-mythium-size-selector]');
    baseIndicatorEl = root.querySelector('[data-mythium-base-indicator]');
    quantityInput  = root.querySelector('[data-mythium-quantity]');
    addBtn         = root.querySelector('[data-mythium-add]');

    setListEl      = root.querySelector('[data-mythium-set-list]');
    setEmptyEl     = root.querySelector('[data-mythium-set-empty]');
    setTotalsEl    = root.querySelector('[data-mythium-set-totals]');
    setTotalEl     = root.querySelector('[data-mythium-set-total]');
    checkoutBtn    = root.querySelector('[data-mythium-checkout]');

    return true;
  }

  // ── Dropzone ───────────────────────────────────────────────────────────
  function bindDropzone() {
    dropzone.addEventListener('click', () => fileInput.click());
    dropzone.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        fileInput.click();
      }
    });

    fileInput.addEventListener('change', (e) => {
      const file = e.target.files && e.target.files[0];
      if (file) handleFile(file);
      fileInput.value = '';
    });

    ['dragenter', 'dragover'].forEach((evt) => {
      dropzone.addEventListener(evt, (e) => {
        e.preventDefault();
        dropzone.dataset.mythiumDragover = 'true';
      });
    });

    ['dragleave', 'drop'].forEach((evt) => {
      dropzone.addEventListener(evt, (e) => {
        e.preventDefault();
        delete dropzone.dataset.mythiumDragover;
      });
    });

    dropzone.addEventListener('drop', (e) => {
      const file = e.dataTransfer.files && e.dataTransfer.files[0];
      if (file) handleFile(file);
    });
  }

  // ── Progress controller ────────────────────────────────────────────────
  // Single source of truth for the processing percentage + status text.
  // setTarget(pct, msg) bumps a monotonic target; current animates toward
  // it via setInterval @ 50 ms with min step 0.4 % so motion is always
  // visible. Capped at 99 % until finish() jumps to 100, flashes "Done!",
  // then clears the overlay.
  const progress = {
    target: 0,
    current: 0,
    message: '',
    intervalId: null,
    reset() {
      this.target = 0;
      this.current = 0;
      this.message = '';
      if (this.intervalId !== null) { clearInterval(this.intervalId); this.intervalId = null; }
      state.draft.processingDisplayUrl = null;
      publishProgress();
    },
    start() {
      if (this.intervalId !== null) return;
      this.intervalId = setInterval(() => this.tick(), 50);
    },
    tick() {
      if (this.current < this.target) {
        const delta = this.target - this.current;
        const step = Math.max(0.4, delta * 0.18);
        this.current = Math.min(this.target, this.current + step);
        publishProgress();
      } else if (this.current >= 100 && this.intervalId !== null) {
        clearInterval(this.intervalId); this.intervalId = null;
      }
    },
    setTarget(targetPct, msg) {
      this.target = Math.max(this.target, Math.min(99, targetPct));
      if (typeof msg === 'string') this.message = msg;
      this.start();
      publishProgress();
    },
    async finish() {
      this.target = 100;
      this.current = 100;
      this.message = 'Done!';
      publishProgress();
      await new Promise(r => setTimeout(r, 300));
      this.reset();
    },
  };

  function publishProgress() {
    state.draft.processingMessage = progress.message || null;
    state.draft.processingPercent = progress.current;
    render();
  }

  function preloadImage(url) {
    return new Promise(resolve => {
      if (!url) return resolve();
      const img = new Image();
      img.onload = resolve;
      img.onerror = resolve;
      img.src = url;
    });
  }

  // ── Alpha analysis ─────────────────────────────────────────────────────
  async function runAnalysis() {
    if (!state.draft.previewUrl || !window.MythiumAlphaAnalyzer) return;
    state.draft.analyzeToken += 1;
    const myToken = state.draft.analyzeToken;
    state.draft.isAnalyzing = true;
    render();

    const mimeType = state.draft.file ? state.draft.file.type : 'image/png';

    try {
      const result = await window.MythiumAlphaAnalyzer.cropArtworkForPreview(
        state.draft.previewUrl,
        mimeType
      );
      // Discard if a newer analysis has been queued (race-condition guard)
      if (myToken !== state.draft.analyzeToken) {
        if (result.wasCropped && result.croppedUrl) URL.revokeObjectURL(result.croppedUrl);
        return;
      }
      revokeBlob(state.draft.croppedPreviewUrl);
      revokeBlob(state.draft.cutlineMaskUrl);
      revokeBlob(state.draft.rimColorUrl);
      state.draft.croppedPreviewUrl = result.croppedUrl;
      state.draft.cutlineMaskUrl = null;
      state.draft.rimColorUrl = null;
    state.draft.rimPadFraction = 0;
      state.draft.aspectRatio = result.aspectRatio;
      state.draft.hasAlpha = result.hasAlpha;
      state.draft.alphaCenterX = result.alphaCenterX;
      state.draft.alphaBottomFraction = result.alphaBottomFraction;
      state.draft.footLeftFraction = result.footLeftFraction;
      state.draft.footRightFraction = result.footRightFraction;
      render();

      // Build the cutline-mask from the same cropped image the renderer
      // will use. Done after the analyzer fields are stored so the user
      // sees the cropped figure immediately; the mask just gates the
      // cutline shape and re-renders when ready.
      if (window.MythiumCutlineMask && result.hasAlpha && result.croppedUrl) {
        try {
          progress.setTarget(65, 'Calculating cutline…');
          await new Promise(r => setTimeout(r, 0));
          const maskUrl = await window.MythiumCutlineMask.buildMask(result.croppedUrl);
          progress.setTarget(80, 'Calculating cutline…');
          if (myToken === state.draft.analyzeToken) {
            state.draft.cutlineMaskUrl = maskUrl;
          } else if (maskUrl) {
            URL.revokeObjectURL(maskUrl);
          }

          // Once we have a mask, build the directional rim-colour PNG.
          if (maskUrl && window.MythiumRimColor && myToken === state.draft.analyzeToken) {
            try {
              progress.setTarget(82, 'Applying acrylic effect…');
              await new Promise(r => setTimeout(r, 0));
              const rim = await window.MythiumRimColor.buildRimColors(result.croppedUrl, maskUrl);
              if (myToken === state.draft.analyzeToken) {
                state.draft.rimColorUrl = rim ? rim.url : null;
                state.draft.rimPadFraction = rim ? rim.padFraction : 0;
                progress.setTarget(99, 'Applying acrylic effect…');
                render();
                // Wait until the rim PNG is decoded so the user actually
                // sees the acryl band at the same moment the spinner
                // hits 100 %.
                if (rim && rim.url) await preloadImage(rim.url);
                await progress.finish();
              } else if (rim && rim.url) {
                URL.revokeObjectURL(rim.url);
              }
            } catch (e) { console.warn('rim-color failed', e); }
          }
        } catch (e) { console.warn('cutline-mask failed', e); }
      }

      // Fallback finish: when no rim was built (no alpha, or builders
      // failed) the rim block didn't call progress.finish(). Close the
      // overlay here so the upload doesn't stay stuck behind it.
      if (myToken === state.draft.analyzeToken && state.draft.processingDisplayUrl) {
        progress.setTarget(99, 'Finishing…');
        await preloadImage(state.draft.croppedPreviewUrl || state.draft.previewUrl);
        await progress.finish();
      }
    } catch (err) {
      console.error('Alpha analysis failed:', err);
      progress.reset();
    } finally {
      if (myToken === state.draft.analyzeToken) {
        state.draft.isAnalyzing = false;
        render();
      }
    }
  }

  async function handleFile(file) {
    // Validate type
    if (!FILE_LIMITS.allowedTypes.includes(file.type)) {
      return setError('Invalid file type. PNG or JPG only.');
    }
    // Validate size
    if (file.size > FILE_LIMITS.maxBytes) {
      return setError('File exceeds 50MB limit.');
    }

    clearError();

    revokeBlob(state.draft.previewUrl);
    state.draft.file = file;
    state.draft.previewUrl = URL.createObjectURL(file);

    // Kick off the progress overlay immediately so the upload stays
    // visible behind the spinner all the way through analysis + masking.
    progress.reset();
    state.draft.processingDisplayUrl = state.draft.previewUrl;
    progress.setTarget(5, 'Preparing…');

    // Load dimensions
    const img = new Image();
    img.onload = () => {
      state.draft.dimensions = { width: img.width, height: img.height };
      validateDimensions();
      render();
      runAnalysis();
    };
    img.src = state.draft.previewUrl;

    // Reset analysis fields
    revokeBlob(state.draft.croppedPreviewUrl);
    revokeBlob(state.draft.cutlineMaskUrl);
    revokeBlob(state.draft.rimColorUrl);
    state.draft.croppedPreviewUrl = null;
    state.draft.cutlineMaskUrl = null;
    state.draft.rimColorUrl = null;
    state.draft.rimPadFraction = 0;
    state.draft.aspectRatio = null;
    state.draft.hasAlpha = false;
    state.draft.alphaCenterX = 0.5;
    state.draft.alphaBottomFraction = 1.0;
    state.draft.footLeftFraction = 0;
    state.draft.footRightFraction = 1;

    render();
  }

  // ── Size + quantity ────────────────────────────────────────────────────
  function bindSizeSelector() {
    sizeSelector.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-size]');
      if (!btn) return;
      const size = btn.dataset.size;
      if (!SIZES[size]) return;
      state.draft.size = size;
      validateDimensions();
      render();
    });
  }

  function bindQuantity() {
    quantityInput.addEventListener('input', () => {
      const val = parseInt(quantityInput.value, 10);
      state.draft.quantity = isNaN(val) || val < 1 ? 1 : val;
      render();
    });
  }

  function validateDimensions() {
    const dims = state.draft.dimensions;
    if (!dims) return;
    const cfg = SIZES[state.draft.size];
    // Orientation-independent: compare the longest / shortest side of the
    // upload against the size's minimums, so portrait and landscape images
    // are judged the same way.
    const longSide = Math.max(dims.width, dims.height);
    const shortSide = Math.min(dims.width, dims.height);
    if (longSide < cfg.minLong || shortSide < cfg.minShort) {
      setError(
        `Resolution too low for ${state.draft.size}. Need at least ${cfg.minLong} × ${cfg.minShort}px (longest × shortest side); your image is ${longSide} × ${shortSide}px.`
      );
    } else {
      clearError();
    }
  }

  // ── Sizing help disclosure ─────────────────────────────────────────────
  function bindSizingHelp() {
    const toggle = root.querySelector('[data-mythium-sizing-toggle]');
    const panel = root.querySelector('[data-mythium-sizing-panel]');
    if (!toggle || !panel) return;
    toggle.addEventListener('click', () => {
      const expanded = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', String(!expanded));
      panel.hidden = expanded;
    });
  }

  // ── Add / edit / remove minis ──────────────────────────────────────────
  function bindAddButton() {
    addBtn.addEventListener('click', handleSubmit);
  }

  // ── Cart checkout ──────────────────────────────────────────────────────
  function bindCheckoutButton() {
    checkoutBtn.addEventListener('click', handleCheckout);
  }

  async function handleCheckout() {
    if (state.minis.length === 0 || state.isCheckingOut) return;

    state.isCheckingOut = true;
    clearError();
    render();

    try {
      // Build line items array. Shopify /cart/add.js accepts up to 250 items per call.
      const items = state.minis.map((mini) => ({
        id: mini.variantId,
        quantity: mini.quantity,
        properties: {
          '_artwork_ref': mini.artworkRef || '',
          '_artwork_url': mini.artworkUrl || '',
          '_artwork_original_ref': mini.artworkOriginalRef || '',
          '_artwork_original_url': mini.artworkOriginalUrl || '',
          'Size': capitalize(mini.size),
          'Artwork': mini.originalFilename || '',
        },
      }));

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ items: items }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        const errMsg = data.description || data.message || 'Could not add to cart.';
        throw new Error(errMsg);
      }

      // Success: redirect to cart page
      window.location.href = '/cart';
    } catch (err) {
      console.error('Checkout failed:', err);
      setError(err.message || 'Could not add to cart.');
      state.isCheckingOut = false;
      render();
    }
  }

  // Upload a single file to the backend and return the parsed response.
  async function uploadArtwork(file) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('size', state.draft.size);

    const res = await fetch(BACKEND_URL + '/upload', {
      method: 'POST',
      body: formData,
    });

    const data = await res.json().catch(() => ({}));

    if (!res.ok || !data.success) {
      const errMsg = data.message || 'Upload failed. Please try again.';
      if (data.error === 'CONTENT_REJECTED') {
        throw new Error('Image rejected: appears to contain nudity or explicit content. Please upload appropriate artwork.');
      }
      throw new Error(errMsg);
    }

    return data;
  }

  async function handleSubmit() {
    if (state.draft.error) return;
    if (!state.draft.file && !state.editingMiniId) {
      return setError('Please upload an image first.');
    }
    if (state.draft.isUploading) return;
    const cfg = SIZES[state.draft.size];

    // Edit zonder nieuwe file: alleen size/quantity updaten
    if (state.editingMiniId && !state.draft.file) {
      const idx = state.minis.findIndex((m) => m.id === state.editingMiniId);
      if (idx !== -1) {
        state.minis[idx] = {
          ...state.minis[idx],
          size: state.draft.size,
          quantity: state.draft.quantity,
          priceCents: cfg.priceCents,
          variantId: cfg.variantId,
        };
      }
      state.editingMiniId = null;
      render();
      return;
    }

    // New mini OR edit with new file: upload to backend
    state.draft.isUploading = true;
    clearError();
    render();

    try {
      const data = await uploadArtwork(state.draft.file);

      // There is no separate background-removed version any more — the
      // uploaded file IS the original. Fulfilment still expects both the
      // artwork and artwork-original properties, so we populate both from
      // the same upload with identical values.
      const newMini = {
        id: crypto.randomUUID(),
        previewUrl: state.draft.croppedPreviewUrl || state.draft.previewUrl,
        rawPreviewUrl: state.draft.previewUrl,
        artworkRef: data.ref,
        artworkUrl: BACKEND_URL + data.uploadUrl,
        artworkOriginalRef: data.ref,
        artworkOriginalUrl: BACKEND_URL + data.uploadUrl,
        originalFilename: data.originalFilename || state.draft.file.name,
        size: state.draft.size,
        quantity: state.draft.quantity,
        priceCents: cfg.priceCents,
        variantId: cfg.variantId,
        imageWidth: data.width || state.draft.dimensions?.width || 0,
        imageHeight: data.height || state.draft.dimensions?.height || 0,
        hasAlpha: state.draft.hasAlpha,
        aspectRatio: state.draft.aspectRatio,
        alphaCenterX: state.draft.alphaCenterX,
        alphaBottomFraction: state.draft.alphaBottomFraction,
        footLeftFraction: state.draft.footLeftFraction,
        footRightFraction: state.draft.footRightFraction,
      };

      if (state.editingMiniId) {
        const idx = state.minis.findIndex((m) => m.id === state.editingMiniId);
        if (idx !== -1) {
          // Keep existing ID, replace rest
          newMini.id = state.editingMiniId;
          state.minis[idx] = newMini;
        }
        state.editingMiniId = null;
      } else {
        state.minis.push(newMini);
      }

      // Reset draft
      state.draft = createEmptyDraft();
      state.draft.previewUrl = null;
      fileInput.value = '';
    } catch (err) {
      console.error('Upload failed:', err);
      setError(err.message || 'Upload failed.');
    } finally {
      state.draft.isUploading = false;
      render();
    }
  }

  function handleEdit(id) {
    const mini = state.minis.find((m) => m.id === id);
    if (!mini) return;
    state.editingMiniId = id;
    state.draft.size = mini.size;
    state.draft.quantity = mini.quantity;
    state.draft.dimensions = { width: mini.imageWidth, height: mini.imageHeight };
    render();
    root.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function handleRemove(id) {
    const mini = state.minis.find((m) => m.id === id);
    if (mini && mini.previewUrl && mini.previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(mini.previewUrl);
    }
    state.minis = state.minis.filter((m) => m.id !== id);
    if (state.editingMiniId === id) state.editingMiniId = null;
    render();
  }

  // ── Render ─────────────────────────────────────────────────────────────
  function render() {
    renderError();
    renderPreview();
    renderSizeSelector();
    renderQuantity();
    renderAddButton();
    renderSet();
  }

  function renderError() {
    if (state.draft.error) {
      errorEl.textContent = state.draft.error;
      errorEl.hidden = false;
    } else {
      errorEl.hidden = true;
    }
  }

  function renderPreview() {
    if (!state.draft.previewUrl) {
      previewEl.hidden = true;
      previewEl.innerHTML = '';
      return;
    }
    previewEl.hidden = false;

    // During processing: keep the upload visible behind the overlay so
    // the user never sees an intermediate "old line" SVG render between
    // the upload and the final acryl version.
    const displayUrl = state.draft.processingDisplayUrl;
    if (displayUrl) {
      const msg = state.draft.processingMessage;
      const pct = Math.round(state.draft.processingPercent || 0);
      const overlay = msg
        ? `<div class="mythium-preview__processing" role="status" aria-live="polite">
             <div class="mythium-preview__spinner"></div>
             <div class="mythium-preview__status">${msg}</div>
             <div class="mythium-preview__progress-bar"><div class="mythium-preview__progress-fill" style="width:${pct}%"></div></div>
           </div>`
        : '';
      previewEl.innerHTML =
        `<img src="${displayUrl}" alt="Artwork preview" style="max-width:100%;max-height:50rem;height:auto;object-fit:contain"/>` + overlay;
      return;
    }

    if (!window.MythiumPreviewRenderer) {
      previewEl.innerHTML = `<img src="${state.draft.previewUrl}" alt="Artwork preview" style="max-width:100%;max-height:50rem;object-fit:contain"/>`;
      return;
    }

    const sourceUrl = state.draft.croppedPreviewUrl || state.draft.previewUrl;
    const ar = state.draft.aspectRatio || (state.draft.dimensions
      ? state.draft.dimensions.width / state.draft.dimensions.height
      : 0.75);

    const figureHtml = window.MythiumPreviewRenderer.renderPreview({
      imageUrl: sourceUrl,
      cutlineMaskUrl: state.draft.cutlineMaskUrl,
      rimColorUrl: state.draft.rimColorUrl,
      rimPadFraction: state.draft.rimPadFraction,
      size: state.draft.size,
      aspectRatio: ar,
      hasAlpha: state.draft.hasAlpha,
      alphaCenterX: state.draft.alphaCenterX,
      alphaBottomFraction: state.draft.alphaBottomFraction,
      footLeftFraction: state.draft.footLeftFraction,
      footRightFraction: state.draft.footRightFraction,
    });

    previewEl.innerHTML = figureHtml;
  }

  function renderSizeSelector() {
    sizeSelector.querySelectorAll('[data-size]').forEach((btn) => {
      btn.setAttribute('aria-pressed', btn.dataset.size === state.draft.size ? 'true' : 'false');
    });
    if (baseIndicatorEl && window.MythiumPreviewRenderer && window.MythiumPreviewRenderer.renderBaseIndicator) {
      baseIndicatorEl.innerHTML = window.MythiumPreviewRenderer.renderBaseIndicator(state.draft.size);
    }
  }

  function renderQuantity() {
    if (parseInt(quantityInput.value, 10) !== state.draft.quantity) {
      quantityInput.value = state.draft.quantity;
    }
  }

  function renderAddButton() {
    const canAdd = (state.draft.file || state.editingMiniId) && !state.draft.error && !state.draft.isUploading;
    addBtn.disabled = !canAdd;
    if (state.draft.isUploading) {
      addBtn.textContent = 'Uploading...';
    } else if (state.editingMiniId) {
      addBtn.textContent = 'Update mini';
    } else {
      addBtn.textContent = 'Add to Your Set';
    }
  }

  function renderSet() {
    const minis = state.minis;
    if (minis.length === 0) {
      setListEl.innerHTML = '';
      setListEl.appendChild(setEmptyEl);
      setTotalsEl.hidden = true;
      checkoutBtn.disabled = true;
      return;
    }
    if (setEmptyEl.parentNode) setEmptyEl.remove();
    setListEl.innerHTML = '';
    let totalCents = 0;
    minis.forEach((mini) => {
      totalCents += mini.priceCents * mini.quantity;
      setListEl.appendChild(buildSetItem(mini));
    });
    setTotalsEl.hidden = false;
    setTotalEl.textContent = formatPrice(totalCents);
    checkoutBtn.disabled = state.isCheckingOut;
    checkoutBtn.textContent = state.isCheckingOut ? 'Adding to cart...' : 'Add all to cart';
  }

  function buildSetItem(mini) {
    const li = document.createElement('li');
    li.className = 'mythium-set__item';
    // Show the uploaded artwork's die-cut preview (the object URL the
    // configurator already built); fall back to the brand logo only if a
    // mini somehow has no preview.
    const thumbUrl = mini.previewUrl || (root && root.dataset.mythiumLogoUrl) || '';
    li.innerHTML = `
      <img class="mythium-set__thumb" src="${thumbUrl}" alt="">
      <div class="mythium-set__info">
        <span class="mythium-set__name" title="${escapeHtml(mini.originalFilename)}">${escapeHtml(mini.originalFilename)}</span>
        <span class="mythium-set__meta">${capitalize(mini.size)} · ${mini.quantity}× · ${formatPrice(mini.priceCents * mini.quantity)}</span>
      </div>
      <div class="mythium-set__actions">
        <button class="mythium-set__action" data-action="edit">Edit</button>
        <button class="mythium-set__action" data-action="remove">Remove</button>
      </div>
    `;
    li.querySelector('[data-action="edit"]').addEventListener('click', () => handleEdit(mini.id));
    li.querySelector('[data-action="remove"]').addEventListener('click', () => handleRemove(mini.id));
    return li;
  }

  // ── Helpers ────────────────────────────────────────────────────────────
  function setError(msg) {
    state.draft.error = msg;
    render();
  }

  function clearError() {
    state.draft.error = null;
  }

  function revokeBlob(url) {
    if (url && typeof url === 'string' && url.startsWith('blob:')) {
      URL.revokeObjectURL(url);
    }
  }

  function formatPrice(cents) {
    return '€' + (cents / 100).toFixed(2);
  }

  function capitalize(s) {
    return s.charAt(0).toUpperCase() + s.slice(1);
  }

  function escapeHtml(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  // ── Intro modal ────────────────────────────────────────────────────────
  // Shows on page load until the visitor ticks the acknowledgement checkbox
  // and clicks Continue. Acceptance is persisted in sessionStorage so it
  // doesn't reappear during the same browsing session.
  const INTRO_STORAGE_KEY = 'mythium-intro-accepted';

  function bindIntroModal() {
    const modal = document.querySelector('[data-mythium-intro-modal]');
    if (!modal) return;

    // Already accepted this session → keep the modal hidden, skip wiring.
    let accepted = false;
    try { accepted = sessionStorage.getItem(INTRO_STORAGE_KEY) === '1'; }
    catch (e) { /* sessionStorage unavailable (private mode) — show modal anyway */ }
    if (accepted) {
      modal.hidden = true;
      return;
    }

    document.body.classList.add('mythium-intro-open');

    const checkbox = modal.querySelector('[data-mythium-intro-accept]');
    const rightsCheckbox = modal.querySelector('[data-mythium-intro-accept-rights]');
    const continueBtn = modal.querySelector('[data-mythium-intro-continue]');

    if (checkbox && rightsCheckbox && continueBtn) {
      const bothChecked = () => checkbox.checked && rightsCheckbox.checked;
      const updateContinueState = () => {
        continueBtn.disabled = !bothChecked();
      };
      checkbox.addEventListener('change', updateContinueState);
      rightsCheckbox.addEventListener('change', updateContinueState);
      continueBtn.addEventListener('click', () => {
        if (!bothChecked()) return;
        try { sessionStorage.setItem(INTRO_STORAGE_KEY, '1'); } catch (e) { /* ignore */ }
        modal.hidden = true;
        document.body.classList.remove('mythium-intro-open');
      });
    }
  }

  // ── Init ───────────────────────────────────────────────────────────────
  function init() {
    if (!cacheDom()) return;
    applyInjectedPricing();
    bindIntroModal();
    bindDropzone();
    bindSizeSelector();
    bindQuantity();
    bindSizingHelp();
    bindAddButton();
    bindCheckoutButton();
    render();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
